import React, { useState } from 'react';
import { Box } from '@mui/material';

// Derive up to two initials from a business name for the no-logo fallback.
export function getInitials(name) {
    if (!name) return '?';
    const words = String(name).trim().split(/\s+/).filter(Boolean);
    if (!words.length) return '?';
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
}

// Palette for no-image initials tiles. All work with white text.
const INITIAL_COLORS = [
    '#5c6bc0', '#26a69a', '#ef5350', '#ab47bc', '#ffa726',
    '#66bb6a', '#42a5f5', '#ec407a', '#8d6e63', '#78909c',
    '#7e57c2', '#29b6f6', '#ff7043', '#9ccc65', '#26c6da',
];

// Pick a stable color from the palette based on the name, so the same
// product/business always renders the same color but neighbours differ.
export function colorForName(name) {
    const s = String(name || '');
    let h = 0;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
    return INITIAL_COLORS[h % INITIAL_COLORS.length];
}

// Shows the business logo when available, otherwise a colored tile with the
// business initials. Used across the public store and customer order views so a
// vendor always has a consistent visual identity.
export default function BusinessLogo({
    src,
    name,
    size = 44,
    radius = '50%',
    bgcolor,
    color = '#fff',
    onClick,
    sx = {},
}) {
    const [imgError, setImgError] = useState(false);
    const bg = bgcolor || colorForName(name);
    const base = {
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: radius,
        cursor: onClick ? 'pointer' : 'default',
        ...sx,
    };

    if (src && !imgError) {
        return (
            <Box component="img" src={src} alt={name || 'logo'} onClick={onClick}
                onError={() => setImgError(true)}
                sx={{ ...base, objectFit: 'cover', bgcolor: '#f5f5f5' }} />
        );
    }

    return (
        <Box onClick={onClick} sx={{
            ...base,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: bg,
            color,
            fontWeight: 700,
            fontSize: size * 0.4,
            lineHeight: 1,
            textTransform: 'uppercase',
            userSelect: 'none',
        }}>
            {getInitials(name)}
        </Box>
    );
}
